import type { JSX } from 'react';
import type { CategoryDef, DashboardPayload } from '../../dashboard-types.js';
import { cardClassFor, deltaClass, deltaText, severityLabel, type Severity } from '../utils.js';

interface Props {
  data: DashboardPayload;
  sortedCategories: CategoryDef[];
  passesFilter: (c: CategoryDef) => boolean;
  selectedSizes: Set<string>;
  sizeTotal: (c: CategoryDef, sz: string) => number;
  sizeDelta: (c: CategoryDef, sz: string) => number;
  severityClass: (v: number) => Severity;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business サイズ × カテゴリ タブ。各サイズ 1 カード内に選択中カテゴリごとの型数行を並べ、サイズ内シェアを比較する
 * @graph-connects none
 */
export function CardsSizeCategory(props: Props): JSX.Element {
  const sortedCats = props.sortedCategories.filter(props.passesFilter);
  const visibleSizes = props.data.sizes.filter((sz) => props.selectedSizes.has(sz));
  if (visibleSizes.length === 0) {
    return (
      <div className="cards">
        <p style={{ padding: 20, color: 'var(--text-muted)' }}>該当するサイズがありません</p>
      </div>
    );
  }
  return (
    <div className="cards">
      {visibleSizes.map((sz) => {
        const sizeTotalAll = sortedCats.reduce((s, c) => s + props.sizeTotal(c, sz), 0);
        const sizeDeltaAll = sortedCats.reduce((s, c) => s + props.sizeDelta(c, sz), 0);
        const sev = props.severityClass(sizeTotalAll);
        const max = Math.max(...sortedCats.map((c) => props.sizeTotal(c, sz)), 1);
        return (
          <div key={sz} className={`card ${cardClassFor(sev)}`}>
            <div className="card-head">
              <div className="card-head-left">
                <div className="cat-icon" style={{ background: '#eef3fc', color: '#1f54c7' }}>
                  {sz}
                </div>
                <div className="cat-meta">
                  <span className="cat-name">サイズ {sz}</span>
                  <span className="cat-id">ac_size = {sz} / 全カテゴリ合計</span>
                </div>
              </div>
              <div className="card-summary">
                <span>
                  <b>{sizeTotalAll.toLocaleString()}</b>
                  <small> 型</small>
                </span>
                <span className={`delta ${deltaClass(sizeDeltaAll)}`}>
                  {deltaText(sizeDeltaAll)}
                </span>
              </div>
            </div>
            <div className="row-list">
              {sortedCats.length === 0 ? (
                <p style={{ padding: 8, color: 'var(--text-subtle)', fontSize: 12 }}>
                  該当カテゴリが選択されていません
                </p>
              ) : (
                sortedCats.map((c) => {
                  const v = props.sizeTotal(c, sz);
                  const d = props.sizeDelta(c, sz);
                  const rowSev = props.severityClass(v);
                  const pct = Math.round((v / max) * 100);
                  return (
                    <div key={c.id} className={`pat-row with-cat ${rowSev}`}>
                      <span className="cat-row-label">
                        <span className={`cat-row-mini ${c.cls}`}>{c.short}</span>
                        <span className="cat-row-name">{c.name}</span>
                      </span>
                      <div className="pat-bar-wrap">
                        <div className="pat-bar-track">
                          <div className="pat-bar-fill" style={{ width: `${String(pct)}%` }}></div>
                        </div>
                        <div className="pat-bar-meta">
                          <span>
                            サイズ {sz} 内最大比 {pct}%
                          </span>
                          <span>{severityLabel(rowSev)}</span>
                        </div>
                      </div>
                      <div className="pat-count">
                        <b>{v.toLocaleString()}</b>
                        <small>型</small>
                        <span className={`delta ${deltaClass(d)}`}>{deltaText(d)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="card-foot">
              <span className="threshold-info">
                閾値: <code>warn &lt; {props.data.thresholds.warn}</code> /{' '}
                <code>danger &lt; {props.data.thresholds.danger}</code>
              </span>
              <span>カテゴリ別: {sortedCats.length}行</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
